
const CONVERSION_RATES = {
    g: 1,
    ml: 1, 
    oz: 28.35,
    lb: 453.6,
    cup: 240, 
    tbsp: 15, 
    tsp: 5,   
    serving: 1
};

export const calculateFoodNutrients = (foodItem, sourceDetails = null) => {
    // 1. Resolve Source Data
    // Use external sourceDetails if provided (live data), otherwise fallback to the item itself (snapshot)
    const source = sourceDetails || foodItem;
    
    // 2. Extract Base Metrics
    const baseAmount = parseFloat(source.serving_size) || 100;
    // Allow fallback to std_unit if serving_unit is missing
    // Also check serving_quantity_unit as per backend logic
    const baseUnit = (source.serving_quantity_unit || source.serving_unit || source.std_unit || 'g').toLowerCase();
    const density = parseFloat(source.density) || 1;
    
    // serving_weight / serving_volume tell us the real grams/ml per serving
    let servingWeight = parseFloat(source.serving_weight) || null;
    let servingVolume = parseFloat(source.serving_volume) || null;

    // Fallback: infer effective grams/ml per serving from serving_size + serving_unit
    // when the explicit fields aren't snapshotted (common for recipe ingredients
    // and OpenFoodFacts items). Without this, getMassInGrams(_, 'serving') returns
    // the raw amount, which makes cross-unit conversions wildly wrong until the
    // user manually switches units (which triggers a refresh from the live food doc).
    if (!servingWeight && !servingVolume) {
        const isVolumeUnit = ['ml', 'cup', 'tbsp', 'tsp', 'fl oz'].includes(baseUnit);
        const rate = CONVERSION_RATES[baseUnit];
        if (isVolumeUnit && rate) {
            servingVolume = baseAmount * rate;
            servingWeight = servingVolume * density;
        } else if (baseUnit !== 'serving' && rate) {
            servingWeight = baseAmount * rate;
            servingVolume = density > 0 ? servingWeight / density : servingWeight;
        }
        // baseUnit === 'serving' with no weight info: cannot infer; leave null.
    }

    // 3. Extract Input Metrics
    const qty = parseFloat(foodItem.quantity) || 0;
    const unit = (foodItem.unit || baseUnit).toLowerCase();

    // 4. Calculate Conversion Factor (Input Qty -> Base Qty)
    let factor = 1;
    
    const isVolume = (u) => ['ml', 'cup', 'tbsp', 'tsp', 'milliliters', 'milliliter'].includes(u);
    
    const getMassInGrams = (amount, u) => {
        if (u === 'serving') {
            // Use serving_weight/serving_volume to convert servings to grams
            if (servingWeight) return amount * servingWeight;
            if (servingVolume) return amount * servingVolume * density;
            // No weight info: can't convert meaningfully, return amount as-is
            return amount;
        }
        const rate = CONVERSION_RATES[u] || 1;
        if (isVolume(u)) {
            return amount * rate * density;
        }
        return amount * rate; 
    };

    if (unit === 'serving') {
        // "1 serving" = The full nutritional payload defined in source
        // Just multiply by quantity of servings
        factor = qty; 
    } else if (unit === baseUnit) {
        // Same unit (e.g. 100g input vs 100g base)
        factor = qty / baseAmount;
    } else if (!CONVERSION_RATES[unit] && !['milliliter', 'grams', 'gram'].includes(unit)) {
         // Unknown unit? Treat as serving count (Legacy safety)
         factor = qty;
    } else {
        // Cross-unit conversion (e.g. cups to grams)
        const baseGrams = getMassInGrams(baseAmount, baseUnit);
        const inputGrams = getMassInGrams(qty, unit);
        
        if (baseGrams > 0) {
            factor = inputGrams / baseGrams;
        } else {
            factor = 0; // Prevent division by zero
        }
    }

    // 5. Apply Factor to Macros
    const getVal = (key) => {
        // Preferred: root level
        if (source[key] !== undefined && source[key] !== null) return parseFloat(source[key]);
        // Fallback: nested macros object (some legacy formats)
        if (source.macros && source.macros[key] !== undefined) return parseFloat(source.macros[key]);
        return 0;
    };

    return {
        calories: (getVal('calories') || 0) * factor,
        protein: (getVal('protein') || 0) * factor,
        carbs: (getVal('carbs') || 0) * factor,
        fat: (getVal('fat') || 0) * factor,
        water_ml: (getVal('water_ml') || 0) * factor
    };
};

/**
 * @deprecated Use userProfile.nutrition_targets from the backend instead.
 * 
 * The backend now calculates and returns nutrition_targets in the /user response.
 * This ensures consistency with compliance calculations that use historical weight data.
 * 
 * This function is kept for backwards compatibility only.
 */
export const calculateDailyTargets = (profile) => {
    console.warn('[Nutrition.js] DEPRECATED: calculateDailyTargets called. Use userProfile.nutrition_targets from backend instead.');
    
    if (!profile) return null;
    
    // If the profile already has backend-calculated targets, use those
    if (profile.nutrition_targets) {
        return profile.nutrition_targets;
    }

    const { 
        current_weight_lbs, 
        current_body_fat, 
        recent_workout_count, 
        primary_goal, 
        height, 
        date_of_birth, 
        sex, 
        gender 
    } = profile;
    
    // 1. Data Prep
    const weightKg = (current_weight_lbs || 160) * 0.453592;
    const heightCm = (height || 175);
    const s = (sex || gender || 'Male').toLowerCase();
    
    let age = 30; 
    if (date_of_birth) {
        const diff = Date.now() - new Date(date_of_birth).getTime();
        age = Math.abs(new Date(diff).getUTCFullYear() - 1970);
    }

    // 2. BMR Calculation (Adaptive Selection)
    let bmr = 0;
    if (current_body_fat && current_body_fat > 5) {
        // Katch-McArdle (More accurate if body fat is known, especially for overweight or muscular individuals)
        // Formula: 370 + (21.6 * Lean Body Mass [kg])
        const leanMassKg = weightKg * (1 - (current_body_fat / 100));
        bmr = 370 + (21.6 * leanMassKg);
    } else {
        // Mifflin-St Jeor (Standard fallback)
        bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * age);
        if (s === 'female') bmr -= 161;
        else bmr += 5;
    }

    // 3. TDEE (Total Daily Energy Expenditure) Multiplier
    // Based on actual recent activity volume
    let activityMultiplier = 1.2; // Sedentary/Baseline
    const count = recent_workout_count || 0;
    
    if (count >= 6) activityMultiplier = 1.725; // Very Active (6+ / week)
    else if (count >= 4) activityMultiplier = 1.55; // Moderate (4-5 / week)
    else if (count >= 2) activityMultiplier = 1.375; // Lightly Active (2-3 / week)
    else activityMultiplier = 1.2; // Sedentary (0-1 / week)

    let tdee = bmr * activityMultiplier;

    // 4. Goal Adjustment Strategy
    // We adjust based on explicitly stated Goal OR implicitly derived from Body Fat status.
    
    const goalText = (primary_goal || '').toLowerCase();
    const isBulking = goalText.includes('muscle') || goalText.includes('gain') || goalText.includes('bulk') || goalText.includes('growth');
    const isCutting = goalText.includes('lose') || goalText.includes('fat') || goalText.includes('cut') || goalText.includes('weight') || goalText.includes('slim') || goalText.includes('lean');
    
    // Thresholds for "High" body fat (General fitness guidelines)
    const fatThreshold = s === 'female' ? 32 : 25;
    const isHighBodyFat = current_body_fat && current_body_fat > fatThreshold;

    let targetCalories = tdee;

    if (isHighBodyFat && !isBulking) {
        // AUTO-CUT: If high body fat, default to deficit unless explicitly bulking
        targetCalories = tdee * 0.80; // 20% deficit
    } else if (isCutting) {
        // EXPLICIT CUT
        targetCalories = tdee * 0.85; // 15% deficit (conservative)
    } else if (isBulking) {
        // EXPLICIT BULK
        // Only valid if not extremely overweight, usually. But we honor user request.
        targetCalories = tdee * 1.10; // 10% surplus
    } else {
        // MAINTENANCE / RECOMP
        targetCalories = tdee;
    }

    // 5. Macro Segmentation
    // Protein Priority: High protein is essential for both cutting (satiety/muscle retention) and bulking.
    // Standard: 2.0g-2.2g per kg of LBM or Total Weight?
    // If obese, using Total Weight overestimates. Use LBM or Target Weight. 
    // We'll use LBM if available, or a capped weight metric.
    
    let proteinGrams = 0;
    if (current_body_fat) {
        const leanMassKg = weightKg * (1 - (current_body_fat / 100));
        proteinGrams = leanMassKg * 2.5; // High protein relative to lean mass
    } else {
        // Fallback: 2.0g/kg, but cap at 200g for safety/realism unless huge
        proteinGrams = Math.min(weightKg * 2.0, 250); 
    }
    
    // Fat: 0.8g - 1.0g per kg
    let fatGrams = weightKg * 0.9;
    
    const proteinCals = proteinGrams * 4;
    const fatCals = fatGrams * 9;
    
    // Carbs: Remainder (Fuel for activity)
    let carbCals = targetCalories - (proteinCals + fatCals);
    
    // Safety check: Don't let carbs go negative or too low unreasonably
    if (carbCals < 0) {
        // Adjust fat down if necessary, ensuring minimums
        fatGrams = (targetCalories - proteinCals) / 9;
        carbCals = 0;
    }
    const carbGrams = carbCals / 4;

    const caloriesMin = Math.round(targetCalories * 0.95);
    const caloriesMax = Math.round(targetCalories * 1.05);

    return {
        calories: Math.round(targetCalories),
        calories_min: caloriesMin,
        calories_max: caloriesMax,
        protein: Math.round(proteinGrams),
        carbs: Math.round(carbGrams),
        fat: Math.round(fatGrams),
        details: {
            bmr: Math.round(bmr),
            tdee: Math.round(tdee),
            goal: isBulking ? 'Bulk' : (isCutting || isHighBodyFat ? 'Cut' : 'Maintain'),
            activityLevel: count >= 6 ? 'High' : (count >= 4 ? 'Moderate' : (count >= 2 ? 'Light' : 'Sedentary'))
        }
    };
};

/**
 * Calculate daily nutrition compliance score (0-100).
 * 
 * NOTE: This calculation MUST match the backend's calculateDailyComplianceScore
 * in core/nutrition.js to ensure consistent compliance scores.
 * 
 * Scoring:
 * - When a water target is set: Calories 50%, Protein 30%, Water 20%.
 * - When no water target is set (legacy days): Calories 60%, Protein 40%.
 * - Calories: perfect if within min-max range, penalized for under/over.
 * - Protein and water: perfect if >= target, linear scale if under.
 * - Days with no entries (0 calories) score 0%.
 */
export const calculateComplianceScore = (totals, targets) => {
    if (!targets) return 0;
    
    // Get calorie range (fallback to single target with ±10% if min/max not available)
    const calTarget = targets.calories || 2000;
    const calMin = targets.calories_min ?? Math.round(calTarget * 0.9);
    const calMax = targets.calories_max ?? Math.round(calTarget * 1.1);
    // Protein is minimum-only (no max penalty)
    const proMin = targets.protein || 120;
    const waterTarget = targets.water_ml || 0;
    
    /**
     * Score a metric based on range compliance.
     * - In range: 100%
     * - Under range: linear progression (0% at 0, 100% at min)
     * - Over range: penalize if shouldPenalizeOver is true
     */
    const scoreWithRange = (actual, min, max, shouldPenalizeOver = true) => {
        // Haven't eaten anything: 0%
        if (actual <= 0) return 0;
        
        // Under minimum: linear scale from 0 to min
        if (actual < min) {
            return (actual / min) * 100;
        }
        
        // In range: perfect score
        if (actual >= min && actual <= max) {
            return 100;
        }
        
        // Over maximum
        if (!shouldPenalizeOver) {
            // No penalty for going over (e.g., protein)
            return 100;
        }
        
        // Penalize overflow (e.g., calories)
        // Each 10% over max = -20% score (aggressive penalty for overeating)
        const overBy = actual - max;
        const overPct = overBy / max;
        const penalty = overPct * 200; // 10% over = 20 point penalty
        return Math.max(0, 100 - penalty);
    };
    
    // Calories: penalize both under AND over
    const calScore = scoreWithRange(totals.calories, calMin, calMax, true);
    
    // Protein: only check if meeting minimum (no max, extra protein is fine)
    const proScore = totals.protein >= proMin ? 100 : (totals.protein / proMin) * 100;
    
    if (waterTarget > 0) {
        const actualWater = totals.water_ml || 0;
        const waterScore = actualWater >= waterTarget ? 100 : (actualWater / waterTarget) * 100;
        // Weighted Average: Calories 50%, Protein 30%, Water 20%
        return Math.round((calScore * 0.5) + (proScore * 0.3) + (waterScore * 0.2));
    }
    
    // Legacy two-component score (Calories 60%, Protein 40%)
    return Math.round((calScore * 0.6) + (proScore * 0.4));
};
